import {
  appliesTo,
  checkDetails,
  isBlank,
  STANDARD_FIELDS,
  type RegistrationForm,
  type StandardFieldKey,
} from '@ems/contracts/profile-fields'

/**
 * One participant's details while a form edits them, and the checks on them. Pure, so the console
 * and the portal share the rules with `ParticipantFields`, the one renderer.
 */

/** A value while it is being typed: numbers stay text until they are sent. */
export type DraftValue = string | string[] | boolean | undefined

export type ParticipantDraft = {
  fullName: string
  email: string
  standard: Record<StandardFieldKey, string>
  answers: Record<string, DraftValue>
}

export function emptyDraft(): ParticipantDraft {
  return {
    fullName: '',
    email: '',
    standard: {
      phone: '',
      nationality: '',
      badgeName: '',
      jobTitle: '',
      companyName: '',
      dietaryRequirements: '',
    },
    answers: {},
  }
}

/** What a stored answer looks like in the form: a number as its digits, the rest as it is. */
export function draftValue(value: unknown): DraftValue {
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string' || typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
  return undefined
}

/**
 * The answers to send: only the questions this ticket asks, blanks left out, numbers as numbers.
 * A number that does not parse is sent as the text, so the check names it rather than dropping it.
 */
export function answersOf(
  form: RegistrationForm,
  ticketTypeId: string,
  answers: Record<string, DraftValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const field of form.fields) {
    if (field.archived || !appliesTo(field, ticketTypeId)) continue
    const value = answers[field.key]
    if (isBlank(value)) continue
    if (field.type === 'number' && typeof value === 'string') {
      const number = Number(value.trim())
      out[field.key] = Number.isFinite(number) ? number : value
    } else {
      out[field.key] = value
    }
  }
  return out
}

/** Answers the draft holds for questions this ticket does not ask — shown before they are dropped. */
export function droppedAnswers(
  form: RegistrationForm,
  ticketTypeId: string,
  answers: Record<string, DraftValue>,
): string[] {
  return form.fields
    .filter((field) => !field.archived && !appliesTo(field, ticketTypeId))
    .filter((field) => !isBlank(answers[field.key]))
    .map((field) => field.label)
}

/**
 * The same checks the API makes, run here so the message under a field is the server's own —
 * plus the two the form always asks, name and email. `requireComplete` adds the required ones.
 */
export function draftErrors(
  form: RegistrationForm,
  ticketTypeId: string,
  draft: ParticipantDraft,
  requireComplete: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {}
  if (requireComplete && draft.fullName.trim() === '') errors['fullName'] = 'Full name is required.'
  if (requireComplete && draft.email.trim() === '') errors['email'] = 'Email is required.'
  else if (draft.email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
    errors['email'] = 'Enter an email address, like name@example.com.'
  }
  if (ticketTypeId === '') return errors

  const result = checkDetails(
    form,
    {
      ticketTypeId,
      standard: draft.standard,
      answers: answersOf(form, ticketTypeId, draft.answers),
    },
    { requireComplete },
  )
  for (const issue of result.issues) errors[issue.field] ??= issue.message
  return errors
}

/** The standard fields' order, for anything that lists a participant's details. */
export const STANDARD_ORDER = STANDARD_FIELDS.map((field) => field.key)
